import {
  createIdeonAgentCarrierIdentifier,
  createIdeonAgentStateLicense,
  deleteIdeonAgentCarrierIdentifier,
  deleteIdeonAgentStateLicense,
  getIdeonAgent,
  updateIdeonAgent,
  updateIdeonAgentCarrierIdentifier,
  updateIdeonAgentStateLicense,
} from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.client'
import type {
  Agent,
  AgentCarrierIdentifierInput,
  AgentInput,
  AgentStateLicenseInput,
} from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getErrorMessage } from './getErrorMessage'

// Wraps the call-API → toast → refresh → return-boolean pattern shared by
// every add/edit/remove operation. Each CRUD function below reduces to one
// expression that describes only what is actually different about it.
async function runRefreshingMutation(
  op: () => Promise<unknown>,
  successMsg: string,
  failPrefix: string,
  refresh: () => Promise<void>
): Promise<boolean> {
  try {
    await op()
    toast.success(successMsg)
    await refresh()
    return true
  } catch (err) {
    console.error(err)
    toast.error(`${failPrefix}: ${getErrorMessage(err)}`)
    return false
  }
}

export function useIdeonAgent(agentId: string | null | undefined) {
  const [agent, setAgent] = useState<Agent | null>(null)
  // Start in loading state when we know we're about to fetch, so the first
  // paint shows skeletons instead of empty "—" placeholders.
  const [loading, setLoading] = useState<boolean>(Boolean(agentId))
  const [loadError, setLoadError] = useState<string | null>(null)
  const [savingAgent, setSavingAgent] = useState(false)

  // Each load call bumps this version; only the latest call's results are
  // accepted, so a quick refresh or agent switch can't be overwritten by an
  // older in-flight response landing late.
  const loadVersionRef = useRef(0)

  const loadAgent = useCallback(async (id: string) => {
    const version = ++loadVersionRef.current
    setLoading(true)
    setLoadError(null)
    try {
      const data = await getIdeonAgent(id)
      if (version !== loadVersionRef.current) return
      setAgent(data.agent)
    } catch (err) {
      if (version !== loadVersionRef.current) return
      console.error(err)
      const message = getErrorMessage(err)
      setLoadError(message)
      toast.error(`Failed to load agent: ${message}`)
      setAgent(null)
    } finally {
      if (version === loadVersionRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!agentId) {
      loadVersionRef.current++ // invalidate any in-flight load
      setAgent(null)
      setLoadError(null)
      setLoading(false)
      return
    }
    void loadAgent(agentId)
  }, [agentId, loadAgent])

  const refreshAgent = useCallback(async () => {
    if (!agentId) return
    await loadAgent(agentId)
  }, [agentId, loadAgent])

  const saveAgentProfile = useCallback(
    async (patch: AgentInput): Promise<boolean> => {
      if (!agentId) return false
      setSavingAgent(true)
      try {
        const data = await updateIdeonAgent(agentId, { agent: patch })
        setAgent(data.agent)
        toast.success('Agent updated.')
        return true
      } catch (err) {
        console.error(err)
        toast.error(`Save failed: ${getErrorMessage(err)}`)
        return false
      } finally {
        setSavingAgent(false)
      }
    },
    [agentId]
  )

  const addStateLicense = useCallback(
    async (body: AgentStateLicenseInput): Promise<boolean> => {
      if (!agentId) return false
      return runRefreshingMutation(
        () => createIdeonAgentStateLicense(agentId, { stateLicense: body }),
        'State license added.',
        'Add failed',
        refreshAgent
      )
    },
    [agentId, refreshAgent]
  )

  const editStateLicense = useCallback(
    async (
      licenseId: string,
      body: AgentStateLicenseInput
    ): Promise<boolean> => {
      if (!agentId) return false
      return runRefreshingMutation(
        () =>
          updateIdeonAgentStateLicense(agentId, licenseId, {
            stateLicense: body,
          }),
        'State license updated.',
        'Update failed',
        refreshAgent
      )
    },
    [agentId, refreshAgent]
  )

  const removeStateLicense = useCallback(
    async (licenseId: string): Promise<boolean> => {
      if (!agentId) return false
      return runRefreshingMutation(
        () => deleteIdeonAgentStateLicense(agentId, licenseId),
        'State license deleted.',
        'Delete failed',
        refreshAgent
      )
    },
    [agentId, refreshAgent]
  )

  const addCarrierIdentifier = useCallback(
    async (body: AgentCarrierIdentifierInput): Promise<boolean> => {
      if (!agentId) return false
      return runRefreshingMutation(
        () =>
          createIdeonAgentCarrierIdentifier(agentId, {
            carrierIdentifier: body,
          }),
        'Carrier identifier added.',
        'Add failed',
        refreshAgent
      )
    },
    [agentId, refreshAgent]
  )

  const editCarrierIdentifier = useCallback(
    async (
      carrierIdentifierId: string,
      body: { state: string; identifier: string }
    ): Promise<boolean> => {
      if (!agentId) return false
      return runRefreshingMutation(
        () =>
          updateIdeonAgentCarrierIdentifier(agentId, carrierIdentifierId, {
            carrierIdentifier: body,
          }),
        'Carrier identifier updated.',
        'Update failed',
        refreshAgent
      )
    },
    [agentId, refreshAgent]
  )

  const removeCarrierIdentifier = useCallback(
    async (carrierIdentifierId: string): Promise<boolean> => {
      if (!agentId) return false
      return runRefreshingMutation(
        () => deleteIdeonAgentCarrierIdentifier(agentId, carrierIdentifierId),
        'Carrier identifier deleted.',
        'Delete failed',
        refreshAgent
      )
    },
    [agentId, refreshAgent]
  )

  return {
    agent,
    loading,
    loadError,
    savingAgent,
    refreshAgent,
    saveAgentProfile,
    addStateLicense,
    editStateLicense,
    removeStateLicense,
    addCarrierIdentifier,
    editCarrierIdentifier,
    removeCarrierIdentifier,
  }
}
